import { Inject, Injectable, LOCALE_ID, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom, of } from 'rxjs';
import { CountryISO } from '@justin-s/ngx-intl-tel-input';
import { FeatureSetResponse, EmptyFeatureSetResponse, IDFlowFeatureSetFields, FeatureSetField } from '../backend-types/feature-set';
import { DeclarationService } from '../declaration/declaration.service';
import { MteService, CURVE } from '../Services/mte.service';
import { SupportedDocTypes } from '../id-upload/id-upload.service';

export interface AboutVoatzApi {
  name: string;
  version: string;
  country: CountryISO;
  voter_affidavit: boolean;
  org_feature_set: FeatureSetResponse;
  is_vc: boolean;
  vc_list: VotingCenterObject | undefined;
}

export interface VotingCenterObject {
  featureSetEntry: FeatureSetField;
  locale?: string;
  votingCenterList: VotingCenter[];
}

export interface VotingCenter {
  id: string;
  name: string;
  location?: string;
}

export interface BackendPubKeysWithAboutVoatzApiEncoded {
  decoder_ecdh_pubkey: string; // base-64 encoded.
  encoder_ecdh_pubkey: string; // base-64 encoded.
  nonce: string; // a timestamp
  bodyEncoded: string; // The encrypted AboutVoatzApi.
}

export type ElectorFieldName = 'first_name' | 'last_name' | 'email' | 'phone' | 'voter_id' | 'dob';
export const electorFieldNames: ElectorFieldName[] = [ 'first_name', 'last_name', 'email', 'phone' ];

export type VWA_TYPE = 'ORG_1_STEP' | 'ORG_1_STEP_MFA' | 'ORG_2_STEP' | 'ORG_IDV' | 'GLOBAL';

@Injectable({
  providedIn: 'root'
})
export class ApiAboutService {
  public displaySpinnerSubject = new BehaviorSubject<boolean>(false);

  readonly headerImageUrl = signal<string | undefined>(undefined);
  readonly headerTitle = signal<string>("");
  readonly headerDates = signal<string>("");
  signatureRequired: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  aboutInformation: BehaviorSubject<AboutVoatzApi> = new BehaviorSubject<AboutVoatzApi>({
    name: '',
    version: '',
    country: CountryISO.UnitedStates,
    voter_affidavit: false,
    org_feature_set: new EmptyFeatureSetResponse(),
    is_vc: false,
    vc_list: {
      featureSetEntry: {
        fieldName: "VotingCenter",
        fieldTitle: "Voting Center",
        fieldType: "voting_center",
        fieldInvalidMessage: "Please select a valid Voting Center.",
        isMandatory: false,
        isKey: false,
        sendInOidvData: false,
        sendInIdProfile: false,
      },
      locale: "en",
      votingCenterList: []
    }
  });
  dashboardFlowFeatureSet: FeatureSetResponse = new EmptyFeatureSetResponse();
  hasRegistrationStep: boolean = false;
  electionNotYetOpen: boolean = false;
  registrationNotYetOpen: boolean = false;
  electionClosed: boolean = false;
  registrationClosed: boolean = false;
  startTimeDate: Date = new Date();
  endTimeDate: Date = new Date();
  startTime: string = "";
  endTime: string = "";
  idUploadFlow: boolean = false;
  regStartTime: string = "";
  regEndTime: string = "";
  localTimeZone: string = "";
  hasPrintOption: boolean = false;
  vwaFlowType: VWA_TYPE = 'ORG_1_STEP';

  constructor(private readonly http: HttpClient,
    private readonly declarationService: DeclarationService,
    private mteService: MteService,
    @Inject(LOCALE_ID) readonly locale: string) {
      this.showSpinner()
      let date = new Date().toString();
      let tzArray = date.split("(");
      this.localTimeZone = "(" + tzArray[tzArray.length-1];
    }

  showSpinner() {
    this.displaySpinnerSubject.next(true);
  }

  hideSpinner() {
    this.displaySpinnerSubject.next(false);
  }

  getSpinnerVisibility(): Observable<boolean> {
    return this.displaySpinnerSubject.asObservable();
  }  

  async about(locale: string = this.locale): Promise<AboutVoatzApi> {   
    let response: Observable<BackendPubKeysWithAboutVoatzApiEncoded> = new Observable();
    let aboutApi: AboutVoatzApi;
    let decoderKeys: CryptoKeyPair;
    let encoderKeys: CryptoKeyPair;

    if(this.mteService.useMte && this.mteService.needHandshake) {
    // Generate $a \in \N$ and $A = aP \in E(\F_p)$ for decoder and encoder.
    // https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/generateKey
    // Both Keys are of type CryptoKeyPair, with .privateKey and .publicKey as CryptoKey objects.
    // This could be initialized earlier, asynchronously, in order to save a bit of time on the frontend.
      const decoderKeysPromise: Promise<CryptoKeyPair> = window.crypto.subtle.generateKey(CURVE, false, [
        'deriveBits',
      ]);
      const encoderKeysPromise: Promise<CryptoKeyPair> = window.crypto.subtle.generateKey(CURVE, false, [
        'deriveBits',
      ]);
      await Promise.all([decoderKeysPromise, encoderKeysPromise]).then( async ([decoderKeysResolved, encoderKeysResolved]) => {
        decoderKeys = decoderKeysResolved;
        encoderKeys = encoderKeysResolved;
        this.mteService.frontendPubKeys = await this.mteService.initFrontendPubKeys(decoderKeys, encoderKeys, locale);
        response = this.http.post<BackendPubKeysWithAboutVoatzApiEncoded>('/api/about', this.mteService.frontendPubKeys,
        {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'x-client-id': this.mteService.frontendPubKeys.personalization_string }),
          observe: 'body',
          responseType: 'json'
        });
      }).catch((err: any) => {
        switch(err.error.error_type) {
          case 'REGISTRATION_NOT_YET_OPEN':
            this.registrationNotYetOpen = true;
            this.startTimeDate = new Date(err.error.startTime);
            this.endTimeDate = new Date(err.error.endTime);
            this.startTime = this.formatDate(this.startTimeDate);
            this.endTime = this.formatDate(this.endTimeDate);
            break;
          case 'ELECTION_CLOSED':
            this.electionClosed = true;
            this.startTimeDate = new Date(err.error.startTime);
            this.endTimeDate = new Date(err.error.endTime);
            this.startTime = this.formatDate(this.startTimeDate);
            this.endTime = this.formatDate(this.endTimeDate);
            break;
        }
        response = of({
          decoder_ecdh_pubkey: "",
          encoder_ecdh_pubkey: "",
          nonce: "",
          bodyEncoded: "" 
        })
        this.hideSpinner();
        return err;
      });
    }
    // In the case of a switch to another language, this kicks in.
    // We don't need a handshake.
    else {
      response = this.http.post<BackendPubKeysWithAboutVoatzApiEncoded>('/api/about', {locale: locale},
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'x-client-id': this.mteService.frontendPubKeys.personalization_string }),
        observe: 'body',
        responseType: 'json'
      });
    }

    return firstValueFrom(response).then(async (res) => {
      this.mteService.backendPubKeys = {
        decoder_ecdh_pubkey: res.decoder_ecdh_pubkey, 
        encoder_ecdh_pubkey: res.encoder_ecdh_pubkey, 
        nonce: res.nonce};
      // On the first call to this function, we do a handshake.
      // Subsequent calls don't need the handshake.
      if(this.mteService.needHandshake) {
        this.mteService.mteInitialization = await this.mteService.computeSharedEntropy(decoderKeys, encoderKeys, this.mteService.frontendPubKeys, this.mteService.backendPubKeys);

        this.mteService.clientMteStates = this.mteService.createInitialMteState(
          this.mteService.mteInitialization.encoderEntropy,
          this.mteService.mteInitialization.nonce,
          this.mteService.mteInitialization.personalization_string,
          this.mteService.mteInitialization.decoderEntropy,
          this.mteService.mteInitialization.nonce,
          this.mteService.mteInitialization.personalization_string
        );
        this.mteService.needHandshake = false;
      }
      if(this.mteService.useMte) {
        let decryptedResponse = this.mteService.mkeDecryptB64(res.bodyEncoded);
        if(decryptedResponse) {
          aboutApi = JSON.parse(decryptedResponse);
        }
        else{
          throw new Error("HTTP response decryption error.");
        }
      }
      else {
        aboutApi = JSON.parse(res.bodyEncoded);
      }
      this.aboutInformation.next(aboutApi);
      // Uncomment the following line to test id upload flow.
      // aboutApi.org_feature_set.scanSettings?.barcodeTypeToScan.types.push("DL", "ID", "PP");
      // Uncomment the following line to test 1-step with MFA flow.
      // aboutApi.org_feature_set.featureSetName = "TEST";
      // aboutApi.org_feature_set.scanSettings = {
      //   scanToLogin: true,
      //   scanIdString: {strings: ["Scan this QR code with an authenticator app in order to proceed."]},
      //   barcodeTypeToScan: { types: ["QR"]}
      // }
      // aboutApi.org_feature_set.featureSetFields.fields.forEach(item => item.sendInOidvData = true);
      this.idUploadFlow = this.setIdFlow(aboutApi);
      this.setFlowType(aboutApi);
    
      let timeNow = new Date();
      
      this.startTimeDate = this.getElectionStart(aboutApi.org_feature_set);
      this.endTimeDate = this.getElectionEnd(aboutApi.org_feature_set);
      this.startTime = this.formatDate(this.startTimeDate);
      this.endTime = this.formatDate(this.endTimeDate);
      
      if(timeNow < this.startTimeDate){
        this.electionNotYetOpen = true;
      }
      if(timeNow > this.endTimeDate){
        this.electionClosed = true;
      }
      if(aboutApi.org_feature_set.registrationSettings !== undefined) {
        this.hasRegistrationStep = true;
        let regStartDate = this.getRegistrationStart(aboutApi.org_feature_set);
        let regEndDate = this.getRegistrationEnd(aboutApi.org_feature_set);
        this.regStartTime = this.formatDate(regStartDate);
        this.regEndTime = this.formatDate(regEndDate);
        if(timeNow < regStartDate){
          this.registrationNotYetOpen = true;
        }
        if(timeNow > regEndDate){
          this.registrationClosed = true;
        }
      }
      if(aboutApi.org_feature_set.miscSettings?.ballotReturnOptions?.printValue !== undefined ||  aboutApi.org_feature_set.miscSettings?.ballotReturnOptions?.electronicOrPrintValue !== undefined) {
        this.hasPrintOption = true;
      }
      this.declarationService.setDeclarationSettings(aboutApi.org_feature_set.declarationSettings);
      const votingSettings = aboutApi.org_feature_set.miscSettings;
      this.headerTitle.set(votingSettings?.header ?? "");
      if(this.isVotingCenter()){
        this.headerDates.set((votingSettings?.vcSubHeader ?? votingSettings?.subHeader) ?? "");
      } else {
        this.headerDates.set(votingSettings?.subHeader ?? "");
      }
      this.hideSpinner();
      return aboutApi;
    })
    .catch((err: any) => {
      switch(err.error.error_type) {
        case 'REGISTRATION_NOT_YET_OPEN':
          this.registrationNotYetOpen = true;
          this.startTimeDate = new Date(err.error.startTime);
          this.endTimeDate = new Date(err.error.endTime);
          this.startTime = this.formatDate(this.startTimeDate);
          this.endTime = this.formatDate(this.endTimeDate);
          break;
        case 'ELECTION_CLOSED':
          this.electionClosed = true;
          this.startTimeDate = new Date(err.error.startTime);
          this.endTimeDate = new Date(err.error.endTime);
          this.startTime = this.formatDate(this.startTimeDate);
          this.endTime = this.formatDate(this.endTimeDate);
          break;
      }
      this.hideSpinner();
      return aboutApi;
    });
   
  }

  formatDate(date: Date): string {
    return date.toLocaleTimeString(this.locale) + ", " + date.toLocaleDateString(this.locale, { year: 'numeric', month: 'long', day: 'numeric' })
  }

  isIDVflow(): boolean {
    return (this.idUploadFlow);
  }

  setIdFlow(aboutApi: AboutVoatzApi): boolean {
    try {
      let idvOptions = aboutApi.org_feature_set.scanSettings?.barcodeTypeToScan.types.filter(item => (SupportedDocTypes as string[]).includes(item));
      if(idvOptions === undefined) {
        return false;
      } else {
        return (idvOptions.length > 0);
      }
    } catch(e) {
      return false;
    }
  }

  setFlowType(aboutApi: AboutVoatzApi) {
    if(aboutApi.org_feature_set.featureSetName === 'GLOBAL') {
      this.vwaFlowType = 'GLOBAL';
      return 'GLOBAL';
    }
    if ((aboutApi.org_feature_set.scanSettings?.barcodeTypeToScan.types.filter(item => (SupportedDocTypes as string[]).includes(item)) ?? []).length > 0) {
      this.vwaFlowType = 'ORG_IDV';
      aboutApi.org_feature_set.featureSetFields = IDFlowFeatureSetFields;
      return 'ORG_IDV';
    }
    if(aboutApi.org_feature_set.registrationSettings) {
      this.vwaFlowType = 'ORG_2_STEP';
    } else if(aboutApi.org_feature_set.scanSettings?.scanToLogin && this.hasQRScan(aboutApi)) {
      this.vwaFlowType = 'ORG_1_STEP_MFA';
    } else {
      this.vwaFlowType = 'ORG_1_STEP';
    }
    return this.VWAFlowType;
  }

  get VWAFlowType(): VWA_TYPE {
    return this.vwaFlowType;
  }

  hasQRScan(aboutApi: AboutVoatzApi): boolean {
    return aboutApi.org_feature_set.scanSettings?.barcodeTypeToScan.types.includes('QR') ?? false;
  }

  get hasDashboard(): boolean {
    return ['GLOBAL', 'ORG_IDV'].includes(this.VWAFlowType);
  }

  getRegistrationStart(featureSet: FeatureSetResponse | undefined): Date {
    let registrationStartString: string = (featureSet?.miscSettings?.registrationStartTimestamp ?? featureSet?.miscSettings?.electionStartTimestamp) ?? "0";
    return new Date(registrationStartString);
  }
  
  getRegistrationEnd(featureSet: FeatureSetResponse | undefined): Date {
    let registrationEndString: string = (featureSet?.miscSettings?.registrationEndTimestamp ?? featureSet?.miscSettings?.electionEndTimestamp) ?? "8000-01-01";
    return new Date(registrationEndString);
  }
  
  getElectionStart(featureSet: FeatureSetResponse | undefined): Date {
    let electionStartString: string;
    if(this.isVotingCenter()) {
      electionStartString = (featureSet?.miscSettings?.vcStartTimestamp ?? featureSet?.miscSettings?.electionStartTimestamp) ?? "0";
    } else {
      electionStartString = (featureSet?.miscSettings?.electionStartTimestamp) ?? "0";
    }
    return new Date(electionStartString);
  }
  
  getElectionEnd(featureSet: FeatureSetResponse | undefined): Date {
    let electionEndString: string;
    if(this.isVotingCenter()) {
      electionEndString = (featureSet?.miscSettings?.vcEndTimestamp ?? featureSet?.miscSettings?.electionEndTimestamp) ?? "8000-01-01";
    } else {
      electionEndString = (featureSet?.miscSettings?.electionEndTimestamp) ?? "8000-01-01";
    }
    return new Date(electionEndString);
  }

  setElectionStartAndClose(startTime: string, endTime: string) {
    let timeNow = new Date();
    this.startTime = this.formatDate(new Date(startTime));
    this.endTime = this.formatDate(new Date(endTime));
      
    if(timeNow < new Date(this.startTime)){
      this.electionNotYetOpen = true;
    }
    if(timeNow > new Date(this.endTime)){
      this.electionClosed = true;
    }
  }

  isVotingCenter(): boolean {
    return this.aboutInformation.value.is_vc;
  }

  get votingCenterObject(): VotingCenterObject | undefined {
    return this.aboutInformation.value.vc_list;
  }

  get votingCenterList(): VotingCenter[] {
    return this.aboutInformation.value.vc_list?.votingCenterList ?? [];
  }

}